/**
 * SelectionActionBar.tsx
 * Floating action bar that appears at the bottom of the viewport when
 * ≥ 2 cells are selected in the Skill Matrix.
 *
 * Shows the selected cell count, quick-level buttons (L1–L5), a Clear
 * button, and an Escape hint. Disappears when < 2 cells are selected.
 *
 * Uses theme.schedule.skillLevelN tokens for the L1–L5 button backgrounds —
 * no hex literals. The level list is driven by SKILL_LEVELS so the scale
 * (1–5, BUG-2026-0124) is defined in one place.
 */

import React from 'react';
import {
  Box,
  Button,
  Divider,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { SKILL_LEVELS, SKILL_LEVEL_META } from '../../../../constants/scheduleEnums';

interface SelectionActionBarProps {
  selectedCount: number;
  onApplyLevel: (level: number) => void;
  onClear: () => void;
}

export const SelectionActionBar: React.FC<SelectionActionBarProps> = ({
  selectedCount,
  onApplyLevel,
  onClear,
}) => {
  const theme = useTheme();

  // Only render when at least 2 cells are selected.
  if (selectedCount < 2) return null;

  // Map each level to its theme color token (L1–L5 + L9 Approver, Phase C).
  const levelColors: Record<number, string> = {
    1: theme.schedule.skillLevel1,
    2: theme.schedule.skillLevel2,
    3: theme.schedule.skillLevel3,
    4: theme.schedule.skillLevel4,
    5: theme.schedule.skillLevel5,
    9: theme.schedule.skillLevel9,
  };
  const levels: Array<{ level: number; bg: string }> = SKILL_LEVELS.map((level) => ({
    level,
    bg: levelColors[level],
  }));

  return (
    <Paper
      elevation={4}
      sx={{
        position: 'fixed',
        bottom: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 1,
        zIndex: 1300,
        borderRadius: 2,
        boxShadow: 6,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        whiteSpace: 'nowrap',
      }}
    >
      <Typography variant="body2" fontWeight={600}>
        {selectedCount} cell{selectedCount === 1 ? '' : 's'} selected
      </Typography>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {levels.map(({ level, bg }) => (
        <Tooltip
          key={level}
          title={`${SKILL_LEVEL_META[level].label} — ${SKILL_LEVEL_META[level].description}`}
          arrow
          placement="top"
        >
          <Button
            variant="contained"
            size="small"
            onClick={() => onApplyLevel(level)}
            sx={{
              bgcolor: bg,
              color: '#fff',
              minWidth: 36,
              px: 1,
              fontWeight: 700,
              fontSize: '0.75rem',
              '&:hover': {
                bgcolor: bg,
                opacity: 0.85,
              },
            }}
          >
            L{level}
          </Button>
        </Tooltip>
      ))}

      <Button
        variant="outlined"
        size="small"
        onClick={onClear}
        sx={{
          color: theme.schedule.noActivity,
          borderColor: theme.schedule.noActivity,
          fontWeight: 700,
          fontSize: '0.75rem',
          px: 1,
          '&:hover': {
            borderColor: theme.schedule.noActivity,
            bgcolor: 'rgba(0,0,0,0.04)',
          },
        }}
      >
        Clear
      </Button>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      <Typography variant="caption" color="text.secondary">
        Esc to deselect
      </Typography>
    </Paper>
  );
};
